/**
 * The sign-in screen.
 *
 * Login and password are enough: the server signs a platform administrator in
 * to the platform and a client with one account in to that account. A person
 * with several accounts names one explicitly — a valid password alone never
 * grants whichever account happens to be available.
 */
import { KeyRound, Loader2 } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";

import { Button } from "@/design-system/Button";
import { TextField } from "@/design-system/TextField";

import { useSignIn } from "./useSession";

const MESSAGE = {
  wrong: "Не подошло. Проверь логин и пароль. Если у тебя несколько счетов — укажи счёт.",
  locked: "Слишком много попыток. Подожди немного и попробуй снова.",
  service: "Внутренняя ошибка сервиса. Попробуй снова через минуту.",
  unreachable: "Сервер не отвечает. Попробуй снова через минуту.",
} as const;

export function SignInPage(): React.JSX.Element {
  const errorId = useId();
  const firstField = useRef<HTMLInputElement>(null);
  const [scoped, setScoped] = useState(false);
  const [login, setLogin] = useState("");
  const [tenant, setTenant] = useState("");
  const [account, setAccount] = useState("");
  const [password, setPassword] = useState("");
  const { submit, pending, failure } = useSignIn();
  const complete = login !== "" && password !== "" && (!scoped || (tenant !== "" && account !== ""));

  // Focused here rather than with the autoFocus attribute, which browsers
  // honour inconsistently and which is only a hint.
  useEffect(() => {
    firstField.current?.focus();
  }, []);

  return (
    <main className="flex min-h-dvh items-center justify-center bg-background-base p-6">
      <form
        className="w-full max-w-sm space-y-4 rounded-lg border border-border-subtle bg-surface-default p-6"
        onSubmit={(event) => {
          event.preventDefault();
          if (complete && !pending) {
            submit({ login, tenantId: scoped ? tenant : null, accountId: scoped ? account : null, password });
          }
        }}
      >
        <div className="flex items-center gap-2">
          <KeyRound aria-hidden="true" className="size-5 text-text-secondary" />
          <h1 className="text-lg font-semibold text-text-primary">Skryptos</h1>
        </div>

        <TextField
          ref={firstField}
          label="Логин"
          name="login"
          type="text"
          autoComplete="username"
          required
          value={login}
          onChange={(event) => { setLogin(event.target.value); }}
        />

        {/* Told to the password manager and to nothing else. The field is never
            pre-filled by us and its value never leaves this component except
            in the request. */}
        <TextField
          label="Пароль"
          name="password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(event) => { setPassword(event.target.value); }}
          aria-invalid={failure !== null}
          aria-describedby={failure === null ? undefined : errorId}
        />

        <label className="flex cursor-pointer items-center gap-2 text-sm text-text-secondary">
          <input
            type="checkbox"
            className="cursor-pointer"
            checked={scoped}
            onChange={(event) => { setScoped(event.target.checked); }}
          />
          У меня несколько счетов — указать счёт
        </label>
        {scoped && (
          <>
            <TextField
              label="Организация"
              name="tenant"
              autoComplete="organization"
              required
              value={tenant}
              onChange={(event) => { setTenant(event.target.value); }}
            />
            <TextField
              label="Счёт"
              name="account"
              required
              value={account}
              onChange={(event) => { setAccount(event.target.value); }}
            />
          </>
        )}

        {failure !== null && (
          <p id={errorId} role="alert" className="text-sm text-status-negative">
            {MESSAGE[failure]}
          </p>
        )}

        <Button type="submit" variant="primary" disabled={pending || !complete} className="w-full">
          {pending && (
            <Loader2 aria-hidden="true" className="size-4 animate-spin motion-reduce:animate-none" />
          )}
          Войти
        </Button>
      </form>
    </main>
  );
}
